/**
 * TeamSection — how regularly the account is actually worked on.
 *
 * The distinction this section exists to draw: switching campaigns on and
 * off in bulk is administration, and Direct's own automation does most of it
 * at night. Editing keywords, placements, bids and texts inside a specific
 * campaign is optimisation. A log full of the former and empty of the latter
 * looks busy and changes nothing.
 *
 * Плитки показателей собираются здесь, а не приходят из API: это четыре
 * прочтения одних и тех же чисел `edits`, и «сколько дней из скольких»
 * страница считает сама.
 *
 * Норма регулярности — доля дней с правками руками: до 16 % кабинет ведут
 * реже раза в две недели, от 33 % — раз в два-три дня. Те же отсечки, по
 * которым начисляется слагаемое балла «Регулярность работы».
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Stack} from 'ui/components/layout/Stack';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {MEASURE_WIDE, DAYS, dateRange, formatNumber, pluralize} from '../helpers';
import type {Edits} from '../types';
import {ActivityTimeline} from './ActivityTimeline';
import {Disclosure} from './Disclosure';
import {ReportCard} from './ReportCard';
import {SplitCard, SplitPanel} from './SplitCard';
import {StatTile} from './StatTile';

export interface TeamSectionProps {
  edits: Edits;
}

/**
 * Вывод под календарём — тем же кеглем, что и вводный текст любого этажа.
 * Мельче он был бы подписью к картинке, а это не подпись: календарь
 * показывает, КОГДА работали, а эти две фразы говорят, что из этого следует.
 */
const Body = styled(Box)({
  ...text('body.l'),
  color: surfaceVar('onSurfaceLight'),
  // Мера вводного абзаца, а не обычного: этот вывод стоит под календарём во
  // всю его ширину, и узкая колонка под широкой картинкой читается как
  // обрывок, а не как её итог.
  maxWidth: MEASURE_WIDE,
});

const Strong = styled(Box)({
  ...text('label.l'),
  color: surfaceVar('onSurface'),
  display: 'inline',
});


export function TeamSection({edits}: TeamSectionProps) {
  const calendar = edits.calendar ?? [];
  const pauses = edits.pauses ?? [];
  const totalDays = calendar.length;
  const manualDays = edits.manualDays ?? 0;
  const touchedDays = edits.daysWithEdits ?? 0;
  const total = edits.total ?? 0;
  const mass = edits.mass ?? 0;
  const autoShare = total > 0 ? Math.round((mass / total) * 100) : 0;

  // Плитки набраны нейтрально: это счётчики периода, а не оценки. Цвет в
  // отчёте означает «хорошо / тревожно / плохо», и «2 407 правок» ни одним
  // из этих слов не является — покрасив их, мы выдавали суждение там, где
  // его нет. Оценку несёт балл и вывод под календарём.
  const stats = [
    {
      label: 'Правок за период',
      value: formatNumber(total),
      hint: 'Все события истории изменений: и точечные правки, и массовые операции.',
    },
    {
      label: 'Дней с правками',
      value: `${touchedDays} из ${totalDays}`,
      hint: 'Дни, когда в кабинете происходило хоть что-то — включая ночную автоматику.',
    },
    {
      label: 'Правки руками',
      value: `${manualDays} из ${totalDays}`,
      hint: 'Дни, когда специалист менял ставки, ключевые слова, площадки или тексты. Норма — от 16% дней периода.',
    },
    {
      label: 'Массовых операций',
      value: `${autoShare}%`,
      hint: 'Доля статусных операций — включить или остановить пачку объявлений. Значительная часть приходится на ночь: это автоматика Директа, а не оптимизация.',
    },
  ];

  return (
    <Stack gap='m'>
      {/* Показатели читаются как один ряд, а не как четыре карточки: общий
          радиус на группе, а между ячейками — шов в один пиксель. */}
      <SplitCard reflowAt='md'>
        {stats.map(stat => (
          <SplitPanel key={stat.label}>
            <StatTile
              bare
              label={stat.label}
              value={stat.value}
              hint={stat.hint}
            />
          </SplitPanel>
        ))}
      </SplitCard>

      <ReportCard>
        <Stack gap='m'>
          <ActivityTimeline days={calendar} idlePeriods={pauses} />

          <Body>
            Из {pluralize(totalDays, DAYS)} периода команда касалась кабинета{' '}
            <Strong as='span'>{pluralize(touchedDays, DAYS)}</Strong>, а правки руками вносила{' '}
            <Strong as='span'>{pluralize(manualDays, DAYS)}</Strong>.
            {pauses.length > 0 && (
              <>
                {' '}
                Самые длинные простои:{' '}
                {pauses
                  .map(
                    pause => `${dateRange(pause.from, pause.to)} (${pluralize(pause.days, DAYS)})`,
                  )
                  .join(', ')}
                .
              </>
            )}{' '}
            {edits.conclusion}
          </Body>

          <Disclosure
            label='Чем правки специалиста отличаются от операций автоматики'
            labelOpen='Свернуть пояснение'
          >
            <Stack gap='s'>
              <Body>
                <Strong as='span'>Синие дни</Strong> — специалист работал руками: менял ключевые
                слова, чистил площадки, переписывал тексты, двигал ставки. Работа кропотливая, и
                именно она влияет на цену обращения.
              </Body>
              <Body>
                <Strong as='span'>Серые дни</Strong> — массовые операции: включить или выключить
                пачку объявлений одной кнопкой. Значительная часть приходится на 00:00–05:00, то
                есть это автоматика Директа, а не команда. Сами по себе они не говорят, что кабинет
                ведут.
              </Body>
              <Body>
                <Strong as='span'>Красные дни</Strong> — с кабинетом не делали ничего. Чем длиннее
                такая полоса, тем дольше найденные ошибки продолжали тратить бюджет.
              </Body>
            </Stack>
          </Disclosure>
        </Stack>
      </ReportCard>
    </Stack>
  );
}
